/**
 * **Work als installierbare App** ([Z-16](../../../../docs/phasen/zusatz-16-pwa-mein-tag.md),
 * [E-348](../../../../docs/ENTSCHEIDUNGEN.md#e-348)).
 *
 * Der Service Worker liegt als `public/sw.js` neben dem Manifest und kommt unverändert ins
 * `dist/` – was er tut und was nicht, steht dort. Hier wird er nur angemeldet.
 *
 * **Nur im gebauten Stand.** Im Entwicklungsmodus liefert Vite die Module einzeln und ohne
 * Prüfsumme aus; ein Worker, der `/assets/…` zwischenspeichert, hätte dort nichts zu tun und
 * könnte höchstens eine alte Fassung festhalten, wenn jemand zwischen `pnpm dev` und dem
 * gebauten Stand wechselt.
 *
 * **Nach `load`, nicht sofort.** Die Anmeldung selbst kostet nichts, aber der erste Lauf des
 * Workers (`install`) holt die Offline-Seite – das soll nicht mit dem Bestand um die Leitung
 * konkurrieren, der beim Start als Erstes gebraucht wird (Leitplanke 4).
 *
 * Fehler werden verschluckt: eine Anwendung, die ohne Worker läuft, ist dieselbe Anwendung wie
 * bis gestern. Nichts hier darf einen Start verhindern.
 */
export function serviceWorkerAnmelden(): void {
  if (!import.meta.env.PROD) return
  if (!('serviceWorker' in navigator)) return
  window.addEventListener(
    'load',
    () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Ohne Worker läuft Work wie bisher – nur nicht installierbar.
      })
    },
    { once: true },
  )
}

/**
 * **Läuft Work gerade als installierte App?** – für Stellen, die sich im Standalone-Modus
 * anders verhalten dürfen (etwa der Hinweis „zum Startbildschirm hinzufügen", den man dort
 * nicht mehr braucht). Fehlt `matchMedia` (Tests), ist die Antwort nein.
 */
export function laeuftAlsApp(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(display-mode: standalone)').matches
}

/**
 * **Die Zahl am App-Symbol** (Z-16): ungelesene Erwähnungen und Antworten, wie im Reiter
 * „Nachrichten". Android Chrome zeigt sie ohne Rückfrage; iOS erst, wenn die App installiert
 * ist und Mitteilungen erlaubt sind (das hängt an F-86). Ohne Unterstützung tut der Aufruf
 * nichts – auch im Browser-Tab am Rechner.
 */
export function appMarkeSetzen(anzahl: number): void {
  const n = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>
    clearAppBadge?: () => Promise<void>
  }
  if (typeof n.setAppBadge !== 'function' || typeof n.clearAppBadge !== 'function') return
  const zusage = anzahl > 0 ? n.setAppBadge(anzahl) : n.clearAppBadge()
  zusage.catch(() => {
    // Keine Erlaubnis oder kein Standalone – dann eben keine Zahl.
  })
}
